import { type UseInfiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import qs from 'query-string';
import {
  API_ENDPOINT,
  type ClientApiV1RoadmapAuthorUserIdQueryString,
  type ClientApiV1RoadmapAuthorUserIdResponse,
  fetchClientApiV1RoadmapAuthorUserId,
  useClientApiV1RoadmapAuthorUserIdQuery,
} from '../../resources/@types/courseSchema';

const DEFAULT_QUERY_PARAMS = {
  pageNumber: 1,
  pageSize: 6,
};

interface UseClientV1RoadmapAuthorUserIdQuery {
  userId: string;
  queryObject?: Partial<ClientApiV1RoadmapAuthorUserIdQueryString>;
  options?: Parameters<typeof useClientApiV1RoadmapAuthorUserIdQuery>[0]['options'];
}

export function useRoadmapAuthorUserIdQuery({
  userId,
  queryObject,
  options,
}: UseClientV1RoadmapAuthorUserIdQuery) {
  return useClientApiV1RoadmapAuthorUserIdQuery({
    queryKey: getUseRoadmapAuthorUserIdQueryKey({ userId, ...queryObject }),
    url: getUseRoadmapAuthorUserIdQueryUrl({ userId, ...queryObject }), // TODO: 변경이 필요할지도
    options,
  });
}

export const getUseRoadmapAuthorUserIdQueryKey = ({
  userId,
  ...queryObject
}: { userId: string } & Partial<ClientApiV1RoadmapAuthorUserIdQueryString>) => [
  API_ENDPOINT.CLIENT_V1_ROADMAP_AUTHOR_USERID(userId),
  { ...DEFAULT_QUERY_PARAMS, ...queryObject },
];

export const getUseRoadmapAuthorUserIdQueryUrl = ({
  userId,
  ...queryObject
}: { userId: string } & Partial<ClientApiV1RoadmapAuthorUserIdQueryString>) =>
  qs.stringifyUrl({
    url: API_ENDPOINT.CLIENT_V1_ROADMAP_AUTHOR_USERID(userId),
    query: {
      ...DEFAULT_QUERY_PARAMS,
      ...queryObject,
    },
  });

/**
 * ----- 무한 스크롤 쿼리 Hook -----
 */
export const getUseRoadmapAuthorUserIdInfinityQueryKey = ({
  userId,
  ...queryObject
}: { userId: string } & Partial<ClientApiV1RoadmapAuthorUserIdQueryString>) => [
  API_ENDPOINT.CLIENT_V1_ROADMAP_AUTHOR_USERID(userId),
  { ...DEFAULT_QUERY_PARAMS, ...queryObject },
  'infinity',
];

export function useRoadmapAuthorUserIdInfinityQuery({
  userId,
  queryObject,
  options,
}: {
  userId: string;
  queryObject: Partial<ClientApiV1RoadmapAuthorUserIdQueryString>;
  options?: UseInfiniteQueryOptions<ClientApiV1RoadmapAuthorUserIdResponse>;
}) {
  const { data, ...rest } = useInfiniteQuery<ClientApiV1RoadmapAuthorUserIdResponse>(
    getUseRoadmapAuthorUserIdInfinityQueryKey({
      userId,
    }),
    ({ pageParam = 1 }) =>
      fetchClientApiV1RoadmapAuthorUserId(
        getUseRoadmapAuthorUserIdQueryUrl({
          userId,
          pageNumber: Number(pageParam),
          ...queryObject,
        }),
      ),
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage?.data) {
          return 1;
        }

        return lastPage.data.pageNumber >= lastPage.data.totalPage
          ? undefined
          : lastPage.data.pageNumber + 1;
      },
      ...options,
    },
  );

  const _data = data?.pages?.map((page) => page?.data) ?? [];

  return { ...rest, data: _data };
}
